import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { walletAPI, claimsAPI } from '../services/api';
import ReimbursementEstimator from '../components/Claims/ReimbursementEstimator';

const CLAIM_TYPES = ['medical', 'dental', 'optical', 'other'];
const CLAIM_CATEGORIES = ['outpatient', 'inpatient', 'emergency', 'preventive'];
const BENEFIT_TYPES = ['outpatient', 'inpatient', 'dental', 'optical', 'pharmacy', 'other'];

export default function SubmitClaim() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [policies, setPolicies] = useState([]);
  const [policyId, setPolicyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const [submittedClaim, setSubmittedClaim] = useState(null);

  const [form, setForm] = useState({
    claim_type: 'medical',
    claim_category: 'outpatient',
    service_date: new Date().toISOString().slice(0, 10),
    service_provider: '',
    diagnosis_code: '',
    diagnosis_description: '',
    treatment_description: '',
    claimed_amount: '',
    benefit_type: 'outpatient'
  });

  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const list = await walletAPI.listPolicies();
        setPolicies(Array.isArray(list) ? list : (list?.data ?? []));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (activeStep !== 2 || !policyId || !form.claimed_amount) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await claimsAPI.estimate({
          policyId,
          service_date: form.service_date,
          claimed_amount: parseFloat(form.claimed_amount) || 0,
          benefit_type: form.benefit_type
        });
        if (!cancelled) setEstimate(res);
      } catch (e) {
        if (!cancelled) setEstimate(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeStep, policyId, form.claimed_amount, form.service_date, form.benefit_type]);

  const handleNext = async () => {
    setError(null);
    if (activeStep === 2 && !claimId) {
      setLoading(true);
      try {
        const claim = await claimsAPI.create({
          policyId,
          ...form,
          claimed_amount: parseFloat(form.claimed_amount) || 0
        });
        setClaimId(claim?.id);
        setActiveStep((s) => s + 1);
      } catch (e) {
        setError(e.response?.data?.error || e.message || 'Failed to create claim');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (activeStep === 4 && claimId) {
      setLoading(true);
      try {
        const result = await claimsAPI.submit(claimId, { submissionNotes: 'Submitted from app' });
        setSubmittedClaim(result?.claim || result);
        setActiveStep(5);
      } catch (e) {
        setError(e.response?.data?.error || e.response?.data?.message || e.message || 'Submit failed');
      } finally {
        setLoading(false);
      }
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const handleFileSelect = async (e, documentType = 'invoice') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      const res = await walletAPI.uploadClaimDocument(formData);
      const url = res?.url || res;
      if (url) setDocuments((d) => [...d, { document_type: documentType, document_name: file.name, file_url: url }]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const addDocumentToClaim = async () => {
    if (!claimId || documents.length === 0) return;
    setError(null);
    setLoading(true);
    try {
      for (const doc of documents) {
        await claimsAPI.addDocument(claimId, doc);
      }
      setActiveStep((s) => s + 1);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to add documents');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Policy', 'Service details', 'Financial', 'Documents', 'Submit', 'Done'];

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>Submit Claim</Typography>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      {activeStep === 0 && (
        <Paper sx={{ p: 2 }}>
          <FormLabel>Select policy for this claim</FormLabel>
          <RadioGroup value={policyId} onChange={(e) => setPolicyId(e.target.value)} sx={{ mt: 1 }}>
            {policies.filter(p => p.status !== 'cancelled').map((p) => (
              <FormControlLabel
                key={p.id}
                value={p.id}
                control={<Radio />}
                label={`${p.policy_type} — ${p.insurer_name || 'Insurer'} (${p.policy_number})`}
              />
            ))}
          </RadioGroup>
          {policies.length === 0 && <Typography color="textSecondary">No policies. Add a policy from Wallet first.</Typography>}
        </Paper>
      )}

      {activeStep === 1 && (
        <Paper sx={{ p: 2 }}>
          <TextField select fullWidth label="Claim type" value={form.claim_type} onChange={(e) => setForm(f => ({ ...f, claim_type: e.target.value }))} sx={{ mb: 2 }}>
            {CLAIM_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select fullWidth label="Category" value={form.claim_category} onChange={(e) => setForm(f => ({ ...f, claim_category: e.target.value }))} sx={{ mb: 2 }}>
            {CLAIM_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField fullWidth type="date" label="Service date" value={form.service_date} onChange={(e) => setForm(f => ({ ...f, service_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField fullWidth label="Service provider" value={form.service_provider} onChange={(e) => setForm(f => ({ ...f, service_provider: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label="Diagnosis code (ICD-10)" value={form.diagnosis_code} onChange={(e) => setForm(f => ({ ...f, diagnosis_code: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label="Diagnosis description" value={form.diagnosis_description} onChange={(e) => setForm(f => ({ ...f, diagnosis_description: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth multiline rows={2} label="Treatment description" value={form.treatment_description} onChange={(e) => setForm(f => ({ ...f, treatment_description: e.target.value }))} sx={{ mb: 2 }} />
        </Paper>
      )}

      {activeStep === 2 && (
        <Paper sx={{ p: 2 }}>
          <TextField fullWidth type="number" label="Amount claimed" value={form.claimed_amount} onChange={(e) => setForm(f => ({ ...f, claimed_amount: e.target.value }))} inputProps={{ step: 0.01, min: 0 }} sx={{ mb: 2 }} />
          <TextField select fullWidth label="Benefit type" value={form.benefit_type} onChange={(e) => setForm(f => ({ ...f, benefit_type: e.target.value }))} sx={{ mb: 2 }}>
            {BENEFIT_TYPES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </TextField>
          <ReimbursementEstimator estimate={estimate} />
        </Paper>
      )}

      {activeStep === 3 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Required: invoice/receipt</Typography>
          <input accept="image/*,application/pdf" type="file" id="claim-doc" style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'invoice')} disabled={loading} />
          <label htmlFor="claim-doc">
            <Button variant="outlined" component="span" disabled={loading}>{loading ? 'Uploading…' : 'Upload invoice/receipt'}</Button>
          </label>
          {documents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {documents.map((d, i) => (
                <Typography key={i} variant="body2">✓ {d.document_name}</Typography>
              ))}
            </Box>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>Optional: medical report, lab results, prescription. PDF or image, max 10MB.</Typography>
        </Paper>
      )}

      {activeStep === 4 && (
        <Paper sx={{ p: 2 }}>
          <Typography>Review and submit. Your claim will be sent to the insurer.</Typography>
          {documents.length === 0 && <Alert severity="warning" sx={{ mt: 1 }}>Add at least one document (invoice/receipt) before submitting.</Alert>}
        </Paper>
      )}

      {activeStep === 5 && submittedClaim && (
        <Paper sx={{ p: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }}>Claim submitted successfully</Alert>
          <Typography>Claim number: <strong>{submittedClaim.claim_number}</strong></Typography>
          <Typography color="textSecondary">Status: Submitted. Estimated processing: 5–7 business days.</Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={() => navigate(`/claims/${submittedClaim.id}`)}>View claim</Button>
            <Button onClick={() => navigate('/claims')}>Back to claims</Button>
          </Box>
        </Paper>
      )}

      {activeStep < 5 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>Back</Button>
          <Button
            variant="contained"
            onClick={activeStep === 3 ? addDocumentToClaim : handleNext}
            disabled={loading || (activeStep === 0 && !policyId) || (activeStep === 3 && documents.length === 0)}
          >
            {loading ? <CircularProgress size={24} /> : activeStep === 3 ? 'Next: Review' : activeStep === 4 ? 'Submit claim' : 'Next'}
          </Button>
        </Box>
      )}
      <Button sx={{ mt: 2 }} onClick={() => navigate('/claims')}>Cancel</Button>
    </Container>
  );
}
